import type { SocialMediaProviders } from '@ori/login-sidebar';
import type { PersonalMenuInitialData } from '@ori/personal-menu/lib/models/PersonalMenuInitialData';
import type { FC } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { AppConfiguration } from '../models';

interface AppConfigurationProviderProps {
  readonly config: AppConfiguration;
}

export const AppConfigurationContext = createContext<AppConfiguration | undefined>(undefined);

export const AppConfigurationProvider: FC<AppConfigurationProviderProps> = ({
  config,
  children,
}) => {
  const { pbsIconInBurgerMenu } = config;

  const [currentLanguage, setCurrentLanguage] = useState<string>(config.currentLanguage);
  const [graphqlUrl, setGraphqlUrl] = useState<string>(config.graphqlUrl);
  const [tenant, setTenant] = useState<string>(config.tenant);

  const [globalApiUrl, setGlobalApiUrl] = useState<string>(config.globalApiUrl);
  const [isCumulusMarket, setIsCumulusMarket] = useState<boolean>(config.isCumulusMarket);
  const [staticApiUrl, setStaticApiUrl] = useState<string>(config.staticApiUrl);
  const [mobileOfficeApiUrl, setMobileOfficeApiUrl] = useState<string>(config.mobileOfficeApiUrl);
  const [shouldDisplayNotificationIcon, setShouldDisplayNotificationIcon] = useState<boolean>(
    config.shouldDisplayNotificationIcon,
  );
  const [ssoAdLoginUrl, setSsoAdLoginUrl] = useState(config.ssoAdLoginUrl);
  const [identityUrl, setIdentityUrl] = useState<string>(config.identityUrl);

  const [externalLoginName, setExternalLoginName] = useState<string | undefined>(
    config.externalLoginName,
  );
  const [personalMenuInitialData, setPersonalMenuInitialData] = useState<
    PersonalMenuInitialData | null | undefined
  >(config.personalMenuInitialData ?? null);
  const [externalProviderType, setExternalProviderType] = useState<
    SocialMediaProviders | undefined
  >(config.externalProviderType);

  const [consultantNumber, setConsultantNumber] = useState<string | undefined>(
    config.consultantNumber,
  );
  const [consultantType, setConsultantType] = useState<number | undefined>(config.consultantType);
  const [reportsItemName, setReportsItemName] = useState<string | undefined>(
    config.reportsItemName,
  );

  const value = useMemo<AppConfiguration>(
    () => ({
      currentLanguage,
      externalLoginName,
      externalProviderType,
      globalApiUrl,
      graphqlUrl,
      isCumulusMarket,
      mobileOfficeApiUrl,
      personalMenuInitialData,
      shouldDisplayNotificationIcon,
      ssoAdLoginUrl,
      staticApiUrl,
      tenant,
      identityUrl,
      consultantNumber,
      consultantType,
      reportsItemName,
      setExternalLoginName,
      setExternalProviderType,
      setIsCumulusMarket,
      setMobileOfficeApiUrl,
      setShouldDisplayNotificationIcon,
      setSsoAdLoginUrl,
      setPersonalMenuInitialData,
      setIdentityUrl,
      setStaticApiUrl,
      setGlobalApiUrl,
      setTenant,
      setGraphqlUrl,
      setCurrentLanguage,
      pbsIconInBurgerMenu,
      setConsultantNumber,
      setConsultantType,
      setReportsItemName,
    }),
    [
      currentLanguage,
      externalLoginName,
      externalProviderType,
      globalApiUrl,
      graphqlUrl,
      isCumulusMarket,
      mobileOfficeApiUrl,
      personalMenuInitialData,
      shouldDisplayNotificationIcon,
      ssoAdLoginUrl,
      staticApiUrl,
      tenant,
      identityUrl,
      consultantNumber,
      consultantType,
      reportsItemName,
      setExternalLoginName,
      setExternalProviderType,
      setIsCumulusMarket,
      setMobileOfficeApiUrl,
      setShouldDisplayNotificationIcon,
      setSsoAdLoginUrl,
      setPersonalMenuInitialData,
      setIdentityUrl,
      setStaticApiUrl,
      setGlobalApiUrl,
      setTenant,
      setGraphqlUrl,
      setCurrentLanguage,
      pbsIconInBurgerMenu,
      setConsultantNumber,
      setConsultantType,
      setReportsItemName,
    ],
  );

  return (
    <AppConfigurationContext.Provider value={value}> {children} </AppConfigurationContext.Provider>
  );
};

export const useAppConfigurationContext = () => {
  const context = useContext(AppConfigurationContext);

  if (context) {
    return context;
  }
  throw new Error('ConfigurationContext value must be set.');
};
